/**
 * Delning av kalkyler — två lägen.
 *
 * 1. WIZARD-läge: wizardInput + scenario komprimeras till en base64url-
 *    sträng direkt i URL:en (?s=...). Mottagaren regenererar profilen från
 *    wizardInput. Ingen serverlagring behövs eftersom wizardInput är liten.
 *
 * 2. SNAPSHOT-läge (Tibber/CSV): hela timserien skickas till backend som
 *    sparar den och returnerar en kort slug. URL:en blir ?delning=SLUG.
 *    Behövs eftersom 8760-timserien inte ryms i en URL.
 *
 * Sekretess: i snapshot-läget rensas all PII (tibber home-id, adress) innan
 * payloaden skickas till servern.
 */

import type { WizardInput } from "../data-sources/wizardGenerator";
import type { BatteryConfig, GridFeeConfig, Strategy } from "../sim/types";
import type { UserData } from "../types";
import { getApiUrl } from "../api/client";

const VERSION = 1;

// ---- Wizard-läge (inline URL) -------------------------------------------

export interface WizardSharePayload {
  wizardInput: WizardInput;
  scenario: {
    battery: BatteryConfig;
    strategy: Strategy;
    grid: GridFeeConfig;
  };
}

export function encodeWizardShareLink(payload: WizardSharePayload): string {
  const wrapped = { v: VERSION, ...payload };
  const json = JSON.stringify(wrapped);
  const utf8Safe = encodeURIComponent(json);
  const base64 = btoa(utf8Safe);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeWizardShareLink(
  encoded: string,
): WizardSharePayload | null {
  try {
    let base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4 !== 0) base64 += "=";
    const utf8Safe = atob(base64);
    const json = decodeURIComponent(utf8Safe);
    const parsed = JSON.parse(json) as { v: number } & WizardSharePayload;
    if (parsed.v !== VERSION) return null;
    if (!parsed.wizardInput || !parsed.scenario) return null;
    return { wizardInput: parsed.wizardInput, scenario: parsed.scenario };
  } catch (err) {
    console.error("Failed to decode wizard share link:", err);
    return null;
  }
}

// ---- Snapshot-läge (server-side) ----------------------------------------

interface SnapshotPayload {
  v: number;
  hours: Array<{
    from: string;
    importKWh: number;
    exportKWh: number;
    spotPrice: number;
    quarterPrices?: number[];
  }>;
  meta: {
    source: string;
    quality: string;
    priceArea: string;
    periodFrom: string;
    periodTo: string;
    totalImport: number;
    totalExport: number;
  };
  scenario: unknown;
  wizardInput?: unknown;
}

/**
 * Skapa en server-snapshot. Rensar PII och returnerar slug.
 */
export async function createSnapshot(
  userData: UserData,
  scenario: unknown,
): Promise<string> {
  const m = userData.metadata;
  // Bygg anonymiserad payload — INGEN tibber home-id, INGEN adress.
  // Vi droppar kvartspriser i delningen: timpriser räcker för att visa
  // samma kalkyl och payloaden krymper ~5x (viktigt för att hålla oss
  // under serverns body-limit med en full 8760-timserie).
  const payload: SnapshotPayload = {
    v: VERSION,
    hours: userData.hours.map((h) => ({
      from: h.from,
      importKWh: h.importKWh,
      exportKWh: h.exportKWh,
      spotPrice: h.spotPrice,
    })),
    meta: {
      source: m.source,
      quality: m.quality,
      priceArea: m.tibberHome?.priceArea ?? "SE3",
      periodFrom: m.periodFrom,
      periodTo: m.periodTo,
      totalImport: m.totalImport,
      totalExport: m.totalExport,
    },
    scenario,
    ...(m.wizardInput && { wizardInput: m.wizardInput }),
  };

  const res = await fetch(`${getApiUrl()}/api/share`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Okänt fel" }));
    throw new Error((err as { error?: string }).error ?? "Kunde inte dela");
  }
  const data = (await res.json()) as { slug: string };
  return data.slug;
}

/**
 * Hämta en server-snapshot via slug.
 */
export async function fetchSnapshot(slug: string): Promise<SnapshotPayload> {
  const res = await fetch(`${getApiUrl()}/api/share/${slug}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Okänt fel" }));
    throw new Error((err as { error?: string }).error ?? "Kunde inte hämta");
  }
  return (await res.json()) as SnapshotPayload;
}

/**
 * Konvertera en hämtad snapshot tillbaka till UserData.
 */
export function snapshotToUserData(snap: SnapshotPayload): UserData {
  return {
    metadata: {
      source: snap.meta.source as UserData["metadata"]["source"],
      fetchedAt: new Date().toISOString(),
      resolution: snap.hours[0]?.quarterPrices ? "quarter" : "hour",
      quality: snap.meta.quality as UserData["metadata"]["quality"],
      periodFrom: snap.meta.periodFrom,
      periodTo: snap.meta.periodTo,
      hourCount: snap.hours.length,
      totalImport: snap.meta.totalImport,
      totalExport: snap.meta.totalExport,
      tibberHome: {
        id: `shared-${crypto.randomUUID()}`,
        nickname: null,
        address: { address1: null, postalCode: null, city: null },
        gridArea: null,
        priceArea: snap.meta.priceArea as
          | "SE1"
          | "SE2"
          | "SE3"
          | "SE4",
      },
      ...(snap.wizardInput
        ? { wizardInput: snap.wizardInput as UserData["metadata"]["wizardInput"] }
        : {}),
    },
    hours: snap.hours.map((h) => ({
      from: h.from,
      importKWh: h.importKWh,
      exportKWh: h.exportKWh,
      spotPrice: h.spotPrice,
      ...(h.quarterPrices && { quarterPrices: h.quarterPrices }),
    })),
  };
}
